import { isString } from 'lodash';
import convert from 'convert';
import { keysToSnakeCaseDeep } from '@agro1desenvolvimento/utils-js';
import {
  RecomendacaoSemeadura,
  RecomendacaoSemeaduraInsumo,
  RecomendacaoSemeaduraInsumoParametros,
  RecomendacaoSemeaduraParametro,
  RecomendacaoSemeaduraQuantidade,
  RecomendacaoSemeaduraSimple,
  RecomendacaoSemeaduraSituacao,
  RecomendacaoSemeaduraStatus,
  RecomendacaoSemeaduraStatusType,
} from '../@types';
import aqilaApi from './aqila-api';

export type FilterRecomendacoesType = {
  safra?: number;
  status?: RecomendacaoSemeaduraStatusType;
  propriedade?: number;
  page?: number;
};

export type RecomendacaoSemeaduraCultivarToSave = {
  insumoId: number;
  insumoTipo: number;
  glebaId: number;
  area: number;
  dataInicio: Date;
  dataFim: Date;
  dose: number;
  parametros: RecomendacaoSemeaduraInsumoParametros;
  itemEstoque?: {
    id: number;
    descricao: string;
  };
};

export type RecomendacaoSemeaduraAduboParametrosToSave = {
  regulagem: number;
  regulagemMaxima: number;
  regulagemMinima: number;
  total: number;
};

export type RecomendacaoSemeaduraAduboToSave = {
  insumoId: number;
  insumoTipo: number;
  glebaId: number;
  area: number;
  dose: number;
  parametros: RecomendacaoSemeaduraAduboParametrosToSave;
};

export type RecomendacaoSemeaduraToSave = {
  status: RecomendacaoSemeaduraStatusType;
  situacao: RecomendacaoSemeaduraSituacao;
  safraId: number;
  periodoId?: number;
  atividadeId: number;
  propriedadeId: number;
  parametros: RecomendacaoSemeaduraParametro;
  adubosAttributes: RecomendacaoSemeaduraAduboToSave[];
  cultivaresAttributes: RecomendacaoSemeaduraCultivarToSave[];
};

type StatusParams = {
  id: number;
  propriedadeId: number;
};

const TAXA_VARIACAO_MINIMA = 0.97;
const TAXA_VARIACAO_MAXIMA = 1.03;

class RecomendacoesSemeadura {
  private readonly endpoint = 'v2/recomendacoes_semeadura';

  async create(record: RecomendacaoSemeaduraToSave) {
    await aqilaApi.post(
      this.endpoint,
      keysToSnakeCaseDeep({ recomendacaoSemeadura: record }),
    );
  }

  async update(record: RecomendacaoSemeaduraToSave & { id: string }) {
    await aqilaApi.patch(
      `${this.endpoint}/${record.id}`,
      keysToSnakeCaseDeep({ recomendacaoSemeadura: record }),
    );
  }

  async status({ id, propriedadeId }: StatusParams) {
    const { data } = await aqilaApi.get<RecomendacaoSemeaduraStatus>(
      `${this.endpoint}/${id}/status`,
      { params: { propriedadeId } },
    );

    return data;
  }

  private parseInsumo(
    insumo: RecomendacaoSemeaduraInsumo,
  ): RecomendacaoSemeaduraInsumo {
    return {
      ...insumo,
      dose: isString(insumo.dose) ? +insumo.dose : insumo.dose,
      area: +insumo.area,
    };
  }

  async list(params: FilterRecomendacoesType = {}) {
    const { data } = await aqilaApi.get<RecomendacaoSemeaduraSimple[]>(
      this.endpoint,
      { params },
    );

    return data;
  }

  async find(id: string) {
    const { data } = await aqilaApi.get<RecomendacaoSemeadura>(
      `${this.endpoint}/${id}`,
    );

    data.cultivares = data.cultivares.map(this.parseInsumo);

    return data;
  }

  async delete(id: string) {
    await aqilaApi.delete(`${this.endpoint}/${id}`);
  }

  getRegulagem({
    distribuicaoRegulagem,
    dose,
    espacamento,
  }: {
    dose: number;
    espacamento: number;
    distribuicaoRegulagem: number;
  }) {
    const result = convert(dose * espacamento * distribuicaoRegulagem, 'm2').to(
      'ha',
    );

    return {
      result,
      min: result * TAXA_VARIACAO_MINIMA,
      max: result * TAXA_VARIACAO_MAXIMA,
    };
  }

  getFatorCorrecao({
    porcentagemCorrecaoDensidade,
    porcentagemAreaRemonte,
    porcentagemCorrecaoQuantidade,
  }: {
    porcentagemCorrecaoDensidade: number;
    porcentagemAreaRemonte: number;
    porcentagemCorrecaoQuantidade: number;
  }) {
    return (
      (porcentagemCorrecaoDensidade / 100) * porcentagemAreaRemonte +
      porcentagemCorrecaoQuantidade
    );
  }

  getDensidadeCorrigida({
    densidade,
    fatorCorrecao,
    pg,
  }: {
    densidade: number;
    pg: number;
    fatorCorrecao: number;
  }) {
    if (fatorCorrecao < 0) return 0;

    return ~~(densidade / (pg / 100) / ((100 - fatorCorrecao) / 100));
  }

  getQuantidade({
    densidadeCorrigida,
    isCulturaPorUnidade,
    pms,
  }: {
    densidadeCorrigida: number;
    pms: number;
    isCulturaPorUnidade: boolean;
  }) {
    const toKg = (value: number) => convert(value, 'g').to('kg');
    let result;

    if (isCulturaPorUnidade) {
      result = densidadeCorrigida;
    } else {
      result = toKg(densidadeCorrigida) * toKg(pms);
    }

    const minima = result * TAXA_VARIACAO_MINIMA;
    const maxima = result * TAXA_VARIACAO_MAXIMA;

    return {
      minima,
      maxima,
      resultado: result,
    };
  }

  getQuantidadeTotal({
    quantidade,
    area,
    comMargemSeguranca,
    taxaCorrecao,
  }: {
    quantidade: RecomendacaoSemeaduraQuantidade;
    area: number;
    comMargemSeguranca: boolean;
    taxaCorrecao: number;
  }) {
    const PESO_SACO = 40;
    const quantidadeMaxima = comMargemSeguranca
      ? quantidade.maxima
      : quantidade.resultado;

    const quantidadeTotal =
      quantidadeMaxima * area * ((taxaCorrecao + 100) / 100);

    return {
      total: quantidadeTotal,
      totalSacos: PESO_SACO <= 0 ? 0 : quantidadeTotal / PESO_SACO,
    };
  }

  getTotalAdubo({
    area,
    dose,
    porcentagemAreaRemonte,
  }: {
    dose: number;
    porcentagemAreaRemonte: number;
    area: number;
  }) {
    return dose * area * ((porcentagemAreaRemonte + 100) / 100);
  }

  getQuantidadeRegulagemAdubo({
    dose,
    areaTotal,
    espacamento,
    distribuicaoRegulagemAdubo,
  }: {
    dose: number;
    areaTotal: number;
    espacamento: number;
    distribuicaoRegulagemAdubo: number;
  }) {
    const DISTRIBUICAO_REGULAGEM_ADUBO = 50;
    const distribuicaoRegulagem =
      distribuicaoRegulagemAdubo || DISTRIBUICAO_REGULAGEM_ADUBO;

    const regulagem = convert(
      dose * espacamento * distribuicaoRegulagem,
      'm2',
    ).to('ha');

    return {
      regulagem: convert(regulagem, 'g').to('kg'),
      total: dose * areaTotal,
    };
  }
}

export default new RecomendacoesSemeadura();
